import React, { useEffect, useState } from 'react';
import { UserPlus, ShieldOff, ShieldCheck, KeyRound, LogOut, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { StepUpDialog } from './StepUpDialog';
import { useAuth } from '../context/AuthContext';

export const ROLE_LABELS = {
  superadmin: 'SuperAdmin', editor: 'Editor', soporte: 'Soporte', finanzas: 'Finanzas', revisor_kyc: 'Revisor KYC', legal: 'Legal',
};
const ROLE_OPTIONS = Object.keys(ROLE_LABELS);
const err = (e) => toast.error(e?.response?.data?.detail || 'Operación rechazada');

const RoleSelect = ({ value, onChange, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    className="bg-gray-800 border border-gray-700 rounded-lg px-2 py-1.5 text-white text-sm">
    {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
  </select>
);

const MemberRow = ({ m, isMe, onAction }) => {
  const [role, setRole] = useState(m.admin_role);
  return (
    <tr className="hover:bg-gray-800/60" data-testid={`admin-member-${m.user_id}`}>
      <td className="px-4 py-3 text-sm text-gray-200">{m.email}{isMe && <span className="ml-2 text-xs text-[#FF6B35]">(tú)</span>}</td>
      <td className="px-4 py-3">
        {isMe ? <span className="text-sm text-gray-300">{ROLE_LABELS[m.admin_role]}</span> : (
          <div className="flex items-center gap-2">
            <RoleSelect value={role} onChange={setRole} testid={`member-role-select-${m.user_id}`} />
            {role !== m.admin_role && (
              <Button size="sm" className="bg-[#FF6B35] hover:bg-[#e55a2a]" data-testid={`member-role-save-${m.user_id}`}
                onClick={() => onAction('role', m, { admin_role: role })}>Guardar</Button>
            )}
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-xs">
        <span className={m.is_active ? 'text-green-400' : 'text-red-400'}>{m.is_active ? 'Activo' : 'Desactivado'}</span>
        <span className={`ml-2 ${m.mfa_enabled ? 'text-green-400' : 'text-yellow-400'}`}>{m.mfa_enabled ? 'MFA ✓' : 'MFA pendiente'}</span>
      </td>
      <td className="px-4 py-3 text-xs text-gray-500">{m.last_seen_at ? new Date(m.last_seen_at).toLocaleString('es-PE') : '—'}</td>
      <td className="px-4 py-3 text-right space-x-1 whitespace-nowrap">
        {!isMe && (
          <>
            <Button size="sm" variant="ghost" title={m.is_active ? 'Desactivar' : 'Activar'} data-testid={`member-toggle-${m.user_id}`}
              onClick={() => onAction('status', m, { active: !m.is_active })}>
              {m.is_active ? <ShieldOff className="w-4 h-4 text-yellow-400" /> : <ShieldCheck className="w-4 h-4 text-green-400" />}
            </Button>
            <Button size="sm" variant="ghost" title="Restablecer MFA" data-testid={`member-reset-mfa-${m.user_id}`}
              onClick={() => onAction('reset-mfa', m)}><KeyRound className="w-4 h-4 text-blue-400" /></Button>
            <Button size="sm" variant="ghost" title="Quitar acceso administrativo" data-testid={`member-remove-${m.user_id}`}
              onClick={() => onAction('remove', m)}><Trash2 className="w-4 h-4 text-red-400" /></Button>
          </>
        )}
        <Button size="sm" variant="ghost" title="Cerrar sesiones" data-testid={`member-revoke-${m.user_id}`}
          onClick={() => onAction('revoke', m)}><LogOut className="w-4 h-4 text-gray-400" /></Button>
      </td>
    </tr>
  );
};

const ACTIONS = {
  role: { title: 'Cambiar rol administrativo', run: (m, x, s) => api.put(`/admin/security/members/${m.user_id}/role`, { ...x, ...s }) },
  status: { title: 'Cambiar estado del administrador', run: (m, x, s) => api.put(`/admin/security/members/${m.user_id}/status`, { ...x, ...s }) },
  'reset-mfa': { title: 'Restablecer MFA (deberá enrolarse de nuevo)', run: (m, x, s) => api.post(`/admin/security/members/${m.user_id}/reset-mfa`, s) },
  revoke: { title: 'Cerrar todas sus sesiones', run: (m, x, s) => api.post(`/admin/security/members/${m.user_id}/revoke-sessions`, s) },
  remove: { title: 'Quitar acceso administrativo', run: (m, x, s) => api.delete(`/admin/security/members/${m.user_id}`, { data: s }) },
  add: { title: 'Agregar administrador', run: (m, x, s) => api.post('/admin/security/members', { ...x, ...s }) },
};

export const AdminMembersPanel = () => {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [pending, setPending] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('editor');

  const load = () => api.get('/admin/security/members').then((r) => setMembers(r.data)).catch(err);
  useEffect(() => { load(); }, []);

  const onAction = (kind, m, extra = {}) => setPending({ kind, m, extra });
  const submit = async (stepup) => {
    try {
      await ACTIONS[pending.kind].run(pending.m, pending.extra, stepup);
      toast.success('Operación registrada');
      if (pending.kind === 'add') setNewEmail('');
      load();
      return true;
    } catch (e) { err(e); return false; }
  };

  return (
    <div className="space-y-6" data-testid="admin-members-panel">
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 flex flex-wrap items-end gap-3">
        <label className="block flex-1 min-w-[220px]">
          <span className="text-gray-400 text-sm">Correo de una cuenta registrada (comprador con contraseña)</span>
          <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} data-testid="member-add-email"
            className="mt-1 w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white" />
        </label>
        <RoleSelect value={newRole} onChange={setNewRole} testid="member-add-role" />
        <Button disabled={!newEmail.includes('@')} className="bg-[#FF6B35] hover:bg-[#e55a2a]" data-testid="member-add-btn"
          onClick={() => onAction('add', null, { email: newEmail.trim(), admin_role: newRole })}>
          <UserPlus className="w-4 h-4 mr-1" /> Agregar
        </Button>
        <Button variant="ghost" onClick={load} data-testid="members-refresh"><RefreshCw className="w-4 h-4" /></Button>
      </div>
      <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-800 text-xs text-gray-400 uppercase">
            <tr><th className="px-4 py-3 text-left">Correo</th><th className="px-4 py-3 text-left">Rol</th>
              <th className="px-4 py-3 text-left">Estado</th><th className="px-4 py-3 text-left">Última actividad</th>
              <th className="px-4 py-3 text-right">Acciones</th></tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {members.map((m) => <MemberRow key={m.user_id} m={m} isMe={m.user_id === user?.id} onAction={onAction} />)}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-500">Cada cambio exige motivo y el código actual de tu app autenticadora, y queda en la auditoría.</p>
      <StepUpDialog open={!!pending} title={pending ? ACTIONS[pending.kind].title : ''}
        description={pending?.m?.email || pending?.extra?.email} onSubmit={submit} onClose={() => setPending(null)} />
    </div>
  );
};

export default AdminMembersPanel;
